/**
 * Survey Calculations Utility
 * 
 * This utility provides accurate mathematical calculations for survey data.
 */

/**
 * Calculate the correct average rating from ratings breakdown
 * @param {Object} ratingsBreakdown - Object with rating counts (e.g., {"1": 0, "2": 1, "3": 5, "4": 10, "5": 15})
 * @returns {number|null} - Calculated average or null if invalid data
 */
export const calculateQuestionAverage = (ratingsBreakdown) => {
  // Validate input
  if (!ratingsBreakdown || typeof ratingsBreakdown !== 'object') {
    return null;
  }

  let totalScore = 0;
  let totalResponses = 0;

  // Calculate weighted average from ratings 1-5
  for (let rating = 1; rating <= 5; rating++) {
    const count = parseInt(ratingsBreakdown[rating.toString()]) || 0;
    totalScore += rating * count;
    totalResponses += count;
  }

  return totalResponses > 0 ? totalScore / totalResponses : null;
};

/**
 * Calculate overall average from all questions
 * @param {Array} questions - Array of question objects with ratings_breakdown
 * @returns {number|null} - Overall average or null if no valid questions
 */
export const calculateOverallAverage = (questions) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return null;
  }

  const validAverages = questions
    .map(question => calculateQuestionAverage(question.ratings_breakdown))
    .filter(average => average !== null);

  return validAverages.length > 0 
    ? validAverages.reduce((sum, average) => sum + average, 0) / validAverages.length
    : null;
};

/**
 * Validate ratings breakdown data
 * @param {Object} ratingsBreakdown - Ratings breakdown object
 * @param {number} responseCount - Expected response count
 * @returns {Object} - Validation result with isValid flag and details
 */
export const validateRatingsBreakdown = (ratingsBreakdown, responseCount) => {
  if (!ratingsBreakdown || typeof ratingsBreakdown !== 'object') {
    return { 
      isValid: false, 
      error: 'Missing or invalid ratings breakdown' 
    };
  }

  // Calculate total from ratings breakdown
  const calculatedTotal = Object.values(ratingsBreakdown)
    .reduce((sum, count) => sum + (parseInt(count) || 0), 0);

  // Check if we have any valid ratings
  if (calculatedTotal === 0) {
    return { 
      isValid: false, 
      error: 'No valid rating responses found' 
    };
  }

  return {
    isValid: true,
    calculatedTotal,
    reportedTotal: responseCount || 0
  };
};

/**
 * Round number to 2 decimal places without trailing zeros
 * @param {number} num - Number to round
 * @returns {number} - Rounded number
 */
export const roundToTwo = (num) => {
  if (typeof num !== 'number' || isNaN(num)) {
    return 0;
  }
  return Math.round(num * 100) / 100;
}; 